import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, User, Calendar, CalendarClock, Clock, DollarSign, Ban, CheckCircle } from 'lucide-react';
import {
  getBookingById,
  cancelBooking,
  checkInPlayer,
  completeBooking,
  getVenueAvailability,
  rescheduleBooking,
} from '../services/bookingService';
import { formatDate, formatTime, formatCurrency, getStatusBadgeClass } from '../utils/formatters';
import { getBookingStatusLabel } from '../utils/bookingStatus';

function localDateInputValue(date = new Date()) {
  const timezoneOffset = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 10);
}

function timeToMinutes(value) {
  const [hours, minutes] = String(value || '').split(':').map(Number);
  return Number.isFinite(hours) ? (hours * 60) + (minutes || 0) : null;
}

function minutesToTime(totalMinutes) {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
}

function availableStartTimes(slots, durationMinutes, date) {
  const intervals = (slots || [])
    .filter((slot) => slot.isAvailable !== false)
    .map((slot) => ({
      start: timeToMinutes(slot.startTime),
      end: timeToMinutes(slot.endTime),
    }))
    .filter((slot) => slot.start !== null && slot.end !== null && slot.end > slot.start)
    .sort((first, second) => first.start - second.start);

  return intervals.flatMap((candidate) => {
    const targetEnd = candidate.start + durationMinutes;
    let coveredUntil = candidate.start;

    for (const interval of intervals) {
      if (interval.end <= coveredUntil) continue;
      if (interval.start > coveredUntil) break;
      coveredUntil = Math.max(coveredUntil, interval.end);
      if (coveredUntil >= targetEnd) break;
    }

    if (coveredUntil < targetEnd) return [];

    const startTime = minutesToTime(candidate.start);
    const endTime = minutesToTime(targetEnd);
    if (new Date(`${date}T${startTime}`) <= new Date()) return [];
    return [{ startTime, endTime }];
  });
}

export default function BookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const changeBookingSectionRef = useRef(null);

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showRescheduleForm, setShowRescheduleForm] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleCourts, setRescheduleCourts] = useState([]);
  const [rescheduleCourtId, setRescheduleCourtId] = useState('');
  const [rescheduleSlots, setRescheduleSlots] = useState([]);
  const [selectedRescheduleSlot, setSelectedRescheduleSlot] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);

  useEffect(() => {
    loadBooking();
  }, [id]);

  useEffect(() => {
    if (!showRescheduleForm) return;
    changeBookingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    changeBookingSectionRef.current?.focus({ preventScroll: true });
  }, [showRescheduleForm]);

  async function loadBooking() {
    try {
      const data = await getBookingById(id);
      setBooking(data.booking || data);
    } catch (err) {
      toast.error('Failed to load booking details');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }

  async function handleCancelBooking() {
    if (!cancelReason.trim()) {
      toast.error('Please provide a cancellation reason');
      return;
    }
    setActionLoading(true);
    try {
      await cancelBooking(id, cancelReason);
      toast.success('Booking cancelled');
      setShowCancelForm(false);
      loadBooking();
    } catch (err) {
      toast.error('Failed to cancel booking');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleCheckIn() {
    setActionLoading(true);
    try {
      await checkInPlayer(id);
      toast.success('Player checked in');
      loadBooking();
    } catch (err) {
      toast.error('Failed to check in player');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleComplete() {
    setActionLoading(true);
    try {
      await completeBooking(id);
      toast.success('Booking completed');
      await loadBooking();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to complete booking');
    } finally {
      setActionLoading(false);
    }
  }

  function openRescheduleForm() {
    const originalDate = String(booking.startTime || booking.start || '').slice(0, 10);
    const today = localDateInputValue();
    setRescheduleDate(originalDate >= today ? originalDate : today);
    setRescheduleCourtId(booking.courtId || '');
    setRescheduleCourts([]);
    setSelectedRescheduleSlot(null);
    setShowCancelForm(false);
    setShowRescheduleForm(true);
  }

  useEffect(() => {
    if (!showRescheduleForm || !rescheduleDate || !booking?.venueId) return undefined;

    let active = true;
    async function loadAvailableSlots() {
      setSlotsLoading(true);
      setSelectedRescheduleSlot(null);
      try {
        const data = await getVenueAvailability(booking.venueId, rescheduleDate);
        if (active) {
          const courts = Array.isArray(data) ? data : [];
          setRescheduleCourts(courts);
          setRescheduleCourtId((current) =>
            courts.some((court) => (court.courtId || court.id) === current)
              ? current
              : (courts.find((court) => (court.courtId || court.id) === booking.courtId)?.courtId ||
                courts[0]?.courtId || courts[0]?.id || '')
          );
        }
      } catch (error) {
        if (active) {
          setRescheduleCourts([]);
          setRescheduleSlots([]);
          toast.error(error.response?.data?.message || 'Failed to load available time slots');
        }
      } finally {
        if (active) setSlotsLoading(false);
      }
    }

    loadAvailableSlots();
    return () => { active = false; };
  }, [showRescheduleForm, rescheduleDate, booking]);

  useEffect(() => {
    if (!showRescheduleForm || !rescheduleDate || !booking || !rescheduleCourtId) {
      setRescheduleSlots([]);
      return;
    }
    const court = rescheduleCourts.find(
      (item) => (item.courtId || item.id) === rescheduleCourtId,
    );
    const start = new Date(booking.startTime || booking.start);
    const end = new Date(booking.endTime || booking.end);
    const durationMinutes = Math.max(Math.round((end - start) / 60000), 1);
    setSelectedRescheduleSlot(null);
    setRescheduleSlots(
      availableStartTimes(court?.availableSlots, durationMinutes, rescheduleDate)
    );
  }, [showRescheduleForm, rescheduleDate, rescheduleCourtId, rescheduleCourts, booking]);

  async function handleReschedule() {
    if (!selectedRescheduleSlot) {
      toast.error('Select a new time slot');
      return;
    }

    setActionLoading(true);
    try {
      await rescheduleBooking(
        id,
        `${rescheduleDate}T${selectedRescheduleSlot.startTime}`,
        `${rescheduleDate}T${selectedRescheduleSlot.endTime}`,
        rescheduleCourtId,
      );
      toast.success('Booking moved successfully');
      setShowRescheduleForm(false);
      setSelectedRescheduleSlot(null);
      await loadBooking();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to change booking');
    } finally {
      setActionLoading(false);
    }
  }

  const statusValue = booking
    ? (booking.status || '')
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/checkedin/g, 'checked_in')
    : '';

  const statusStyles = {
    confirmed: 'text-[#15803D] bg-[#DCFCE7]',
    pending: 'text-[#15803D] bg-[#DCFCE7]',
    cancelled: 'text-[#D46C4E] bg-[#FDF1EB]',
    completed: 'text-[#56B7AC] bg-[#E0F7F4]',
    checkedin: 'text-[#264D59] bg-[#E8F4FD]',
    checked_in: 'text-[#264D59] bg-[#E8F4FD]',
  };
  const canManageBooking = ['confirmed', 'pending'].includes(statusValue);
  const canCompleteBooking = statusValue === 'checked_in';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#264D59] border-t-transparent" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="card p-12 text-center">
        <p className="text-[#6B7280]">Booking not found</p>
        <button onClick={() => navigate(-1)} className="btn-primary mt-4">
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="btn-outline">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </button>
        <div>
          <h1 className="text-2xl font-bold text-[#1F2937]">Booking Details</h1>
          <p className="text-sm text-[#6B7280]">Booking #{id?.slice(-8)}</p>
        </div>
      </div>

      <div className="card p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-[#E8F4FD] flex items-center justify-center">
              <User className="h-6 w-6 text-[#264D59]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#1F2937]">
                {booking.playerName || booking.player?.name || 'Player'}
              </h2>
              <p className="text-sm text-[#6B7280]">
                {booking.playerEmail || booking.player?.email || ''}
              </p>
            </div>
          </div>
          <span
            className={`inline-flex items-center px-3 py-1 rounded-[30px] text-sm font-medium ${statusStyles[statusValue] || 'bg-[#F1F5F9] text-[#6B7280]'}`}
          >
            {getBookingStatusLabel(booking.status).toUpperCase()}
          </span>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-[#F8FAFC]">
              <Calendar className="h-5 w-5 text-[#264D59]" />
              <div>
                <p className="text-sm text-[#6B7280]">Date</p>
                <p className="text-sm font-medium text-[#1F2937]">
                  {formatDate(booking.startTime || booking.start || booking.date)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-[#F8FAFC]">
              <Clock className="h-5 w-5 text-[#264D59]" />
              <div>
                <p className="text-sm text-[#6B7280]">Time</p>
                <p className="text-sm font-medium text-[#1F2937]">
                  {formatTime(booking.startTime || booking.start)} - {formatTime(booking.endTime || booking.end)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-[#F8FAFC]">
              <DollarSign className="h-5 w-5 text-[#264D59]" />
              <div>
                <p className="text-sm text-[#6B7280]">Amount</p>
                <p className="text-sm font-medium text-[#1F2937]">
                  {formatCurrency(booking.amount || booking.totalAmount || 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-[#F8FAFC]">
              <p className="text-sm text-[#6B7280]">Venue</p>
              <p className="text-sm font-medium text-[#1F2937]">
                {booking.venueName || booking.venue?.name || 'N/A'}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-[#F8FAFC]">
              <p className="text-sm text-[#6B7280]">Court</p>
              <p className="text-sm font-medium text-[#1F2937]">
                {booking.courtName || booking.court?.name || 'N/A'}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-[#F8FAFC]">
              <p className="text-sm text-[#6B7280]">Payment Status</p>
              <p className="text-sm font-medium text-[#1F2937] capitalize">
                {booking.payment?.status === 'Completed' ? 'Completed' : (booking.payment?.status || 'Completed')}
              </p>
            </div>
          </div>
        </div>

        {booking.notes && (
          <div className="mt-4 p-3 rounded-lg bg-[#F8FAFC]">
            <p className="text-sm text-[#6B7280]">Notes</p>
            <p className="text-sm text-[#1F2937]">{booking.notes}</p>
          </div>
        )}

        <div className="mt-6 pt-6 border-t border-[#E2E8F0]">
          <h3 className="text-sm font-medium text-[#6B7280] mb-3">Status Timeline</h3>
          <div className="space-y-3">
            {['created', 'confirmed', 'checked_in', 'completed'].map((status, idx) => {
              const statusKey = status === 'created' ? 'createdAt' : `${status}At`;
              const timestamp = booking[statusKey];
              const isActive =
                status === 'created' ||
                (status === 'confirmed' && ['confirmed', 'checked_in', 'completed'].includes(statusValue)) ||
                (status === 'checked_in' && ['checked_in', 'completed'].includes(statusValue)) ||
                (status === 'completed' && statusValue === 'completed');

              return (
                <div key={status} className="flex items-center gap-3">
                  <div
                    className={`h-2.5 w-2.5 rounded-full ${
                      isActive ? 'bg-[#56B7AC]' : 'bg-[#E2E8F0]'
                    }`}
                  />
                  <div>
                    <p className={`text-sm font-medium ${isActive ? 'text-[#1F2937]' : 'text-[#6B7280]'}`}>
                      {status.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </p>
                    {timestamp && (
                      <p className="text-xs text-[#6B7280]">{formatDate(timestamp)}</p>
                    )}
                  </div>
                </div>
              );
            })}
            {statusValue === 'cancelled' && (
              <div className="flex items-center gap-3">
                <div className="h-2.5 w-2.5 rounded-full bg-[#D46C4E]" />
                <div>
                  <p className="text-sm font-medium text-[#1F2937]">Cancelled</p>
                  {booking.cancelledAt && (
                    <p className="text-xs text-[#6B7280]">{formatDate(booking.cancelledAt)}</p>
                  )}
                  {booking.cancelReason && (
                    <p className="text-xs text-[#6B7280]">Reason: {booking.cancelReason}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {(canManageBooking || canCompleteBooking) && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-[#1F2937] mb-4">Actions</h3>
          <div className="flex flex-wrap gap-3">
            {statusValue === 'confirmed' && (
              <button
                onClick={handleCheckIn}
                disabled={actionLoading}
                className="btn-primary"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                {actionLoading ? 'Processing...' : 'Check In Player'}
              </button>
            )}
            {canCompleteBooking && (
              <button
                onClick={handleComplete}
                disabled={actionLoading}
                className="btn-primary"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                {actionLoading ? 'Processing...' : 'Complete Booking'}
              </button>
            )}
            {canManageBooking && <>
              <button
                onClick={openRescheduleForm}
                disabled={actionLoading}
                className="btn-secondary"
              >
                <CalendarClock className="h-4 w-4 mr-2" />
                Change Booking
              </button>
              <button
                onClick={() => {
                  setShowCancelForm(!showCancelForm);
                  setShowRescheduleForm(false);
                }}
                className="btn-danger"
              >
                <Ban className="h-4 w-4 mr-2" />
                Cancel Booking
              </button>
            </>}
          </div>

          {showRescheduleForm && (
            <div ref={changeBookingSectionRef} tabIndex={-1} className="mt-4 scroll-mt-6 rounded-xl border border-[#BFE5DF] bg-[#F0FAF9] p-4 outline-none focus:ring-2 focus:ring-[#56B7AC]/50">
              <div className="mb-4">
                <h4 className="font-semibold text-[#1F2937]">Change court or schedule</h4>
                <p className="mt-1 text-sm text-[#64748B]">Move this booking to another court, date, or available time.</p>
              </div>
              <div className="flex flex-wrap items-end gap-4">
                <label className="min-w-[190px] text-sm font-medium text-[#344054]">
                  Court
                  <select
                    value={rescheduleCourtId}
                    onChange={(event) => setRescheduleCourtId(event.target.value)}
                    className="input-field mt-1"
                  >
                    {rescheduleCourts.map((court) => (
                      <option key={court.courtId || court.id} value={court.courtId || court.id}>
                        {court.courtName || court.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="min-w-[190px] text-sm font-medium text-[#344054]">
                  Booking date
                  <input
                    type="date"
                    min={localDateInputValue()}
                    value={rescheduleDate}
                    onChange={(event) => setRescheduleDate(event.target.value)}
                    className="input-field mt-1"
                  />
                </label>
                <p className="pb-2 text-xs text-[#64748B]">
                  Choose another court or time. The booking duration and paid amount stay unchanged.
                </p>
              </div>

              <div className="mt-4">
                <p className="mb-2 text-sm font-medium text-[#344054]">Available time slots</p>
                {slotsLoading ? (
                  <p className="text-sm text-[#64748B]">Loading available slots...</p>
                ) : rescheduleSlots.length === 0 ? (
                  <p className="rounded-lg bg-white p-4 text-sm text-[#64748B]">No time slots can fit this booking on the selected date.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {rescheduleSlots.map((slot) => {
                      const selected = selectedRescheduleSlot?.startTime === slot.startTime;
                      return (
                        <button
                          key={`${slot.startTime}-${slot.endTime}`}
                          type="button"
                          onClick={() => setSelectedRescheduleSlot(slot)}
                          className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                            selected
                              ? 'border-[#264D59] bg-[#264D59] text-white'
                              : 'border-[#BFE5DF] bg-white text-[#285B63] hover:border-[#56B7AC]'
                          }`}
                        >
                          {formatTime(`${rescheduleDate}T${slot.startTime}`)} – {formatTime(`${rescheduleDate}T${slot.endTime}`)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={handleReschedule}
                  disabled={actionLoading || !selectedRescheduleSlot}
                  className="btn-primary disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {actionLoading ? 'Saving changes...' : 'Save Booking Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowRescheduleForm(false)}
                  disabled={actionLoading}
                  className="btn-outline"
                >
                  Discard Changes
                </button>
              </div>
            </div>
          )}

          {showCancelForm && (
            <div className="mt-4 p-4 rounded-lg bg-[#FDF1EB] space-y-3">
              <p className="text-sm text-[#D46C4E]">
                Are you sure? This will cancel the booking and refund the player if applicable.
              </p>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={2}
                className="input-field"
                placeholder="Cancellation reason..."
              />
              <div className="flex gap-2">
                <button
                  onClick={handleCancelBooking}
                  disabled={actionLoading}
                  className="btn-danger"
                >
                  {actionLoading ? 'Processing...' : 'Confirm Cancel'}
                </button>
                <button
                  onClick={() => setShowCancelForm(false)}
                  className="btn-outline"
                >
                  Keep Booking
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
